import { FEATURE_TYPE } from '@/constants/features';
import { UsePointToProjectionResult } from '@/utils/map/usePointToProjection';
import getBoundingBoxPolygon from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/utils/getBoundingBoxPolygon';
import getStyle from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getStyle';
import { TRANSPARENT_COLOR } from '@/components/Map/MapViewer/MapViewer.constants';
import { Feature } from 'ol';
import Point from 'ol/geom/Point';
import { Fill, Style, Text } from 'ol/style';
import type { MarkerText } from 'minerva-plugin-api';
import { getFontName } from '@/components/Map/MapViewer/utils/mapElementsRendering/text/getFontName';
import { TWO } from '@/constants/common';

const DEFAULT_FONT_SIZE = 12;

const measureTextSize = (
  text: string,
  font: string,
  fontSize: number,
): { width: number; height: number } => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) {
    return { width: 0, height: fontSize };
  }
  ctx.font = font;
  const metrics = ctx.measureText(text);
  const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  return { width: metrics.width, height: height || fontSize };
};

export const getMarkerTextFeatures = (
  markers: MarkerText[],
  { pointToProjection }: { pointToProjection: UsePointToProjectionResult },
): Feature<Point>[] =>
  markers.map(marker => {
    const feature = new Feature<Point>({
      geometry: new Point(pointToProjection({ x: marker.x, y: marker.y })),
      id: marker.id,
      type: FEATURE_TYPE.TEXT_MARKER,
    });
    const fontName = getFontName(marker.fontName || 'ARIAL');
    const fontSize = marker.fontSize || DEFAULT_FONT_SIZE;
    const font = `${fontSize}px ${fontName}`;
    const { width, height } = measureTextSize(marker.text, font, fontSize);
    const boundingBoxPolygon = getBoundingBoxPolygon({
      x: marker.x - width / TWO,
      y: marker.y - height / TWO,
      width,
      height,
      pointToProjection,
    });
    feature.setStyle([
      new Style({
        text: new Text({
          text: marker.text,
          font,
          fill: new Fill({ color: marker.color }),
        }),
      }),
      getStyle({
        geometry: boundingBoxPolygon,
        borderColor: TRANSPARENT_COLOR,
        fillColor: TRANSPARENT_COLOR,
      }),
    ]);
    return feature;
  });
